import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useFundFamilyConfigRetrieveSuspense,
  useFundFamilyConfigUpdate,
} from "gen/endpoints/fund-family-config/fund-family-config";
import { FundFamilyConfigEnrichedData } from "gen/model/fundFamilyConfigEnrichedData";
import { FundFamilyConfigUpsertDataRequest } from "gen/model/fundFamilyConfigUpsertDataRequest";
import { HypotheticalPnlInputLevelEnum } from "gen/model/hypotheticalPnlInputLevelEnum";
import { fundFamilyConfigUpdateBody } from "gen/schemas/fund-family-config/fund-family-config";
import { useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import Form from "shared/components/form/Form";
import { FormEntitySelect } from "shared/components/input/select/FormEntitySelect";

const FundFamilyPnlLevelSelectionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  fundFamilyId: string;
}> = ({ fundFamilyId, isOpen, onClose }) => {
  const { t } = useTranslation(["waterfall", "common"]);

  const { data, refetch: refetchConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const { config: fundFamilyConfig } = data;

  const convertToFormValues = useCallback(
    (response: FundFamilyConfigEnrichedData) => ({
      ...response.config,
      hypothetical_pnl_entity_ids:
        response.current_hypothetical_pnl_entities.map(
          (entity) => entity.value,
        ),
    }),
    [],
  );

  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    defaultValues: convertToFormValues(data),
    resolver: zodResolver(fundFamilyConfigUpdateBody),
  });

  const updateFundFamilyConfig = useFundFamilyConfigUpdate();

  const { control } = form;

  const hypotheticalPnlInputLevel = useWatch({
    control,
    name: "hypothetical_pnl_input_level",
  });

  const onSubmit = async (formData: FundFamilyConfigUpsertDataRequest) => {
    await updateFundFamilyConfig.mutateAsync({
      fundFamilyId,
      data: {
        ...fundFamilyConfig,
        hypothetical_pnl_input_level: formData.hypothetical_pnl_input_level,
        hypothetical_pnl_entity_ids:
          formData.hypothetical_pnl_input_level ===
          HypotheticalPnlInputLevelEnum.fund_family
            ? []
            : formData.hypothetical_pnl_entity_ids,
      },
    });
    const { data } = await refetchConfig();
    if (data) {
      form.reset(convertToFormValues(data));
    }
    onClose();
  };

  const closeWithReset = () => {
    form.reset(convertToFormValues(data));
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={closeWithReset} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit}>
          <ModalHeader>
            {t("waterfall:hypotheticalWaterfall.modal.title")}
          </ModalHeader>
          <ModalBody>
            <FormToggleButtonGroup
              shouldUnregister={false}
              name="hypothetical_pnl_input_level"
              title={t(
                "waterfall:hypotheticalWaterfall.modal.inputLevelToggle.label",
              )}
            >
              <ToggleButton value={HypotheticalPnlInputLevelEnum.fund_family}>
                {t(
                  "waterfall:hypotheticalWaterfall.modal.inputLevelToggle.options.fundFamily",
                )}
              </ToggleButton>
              <ToggleButton
                value={HypotheticalPnlInputLevelEnum.custom_entities}
              >
                {t(
                  "waterfall:hypotheticalWaterfall.modal.inputLevelToggle.options.customEntity",
                )}
              </ToggleButton>
            </FormToggleButtonGroup>
            {hypotheticalPnlInputLevel ===
              HypotheticalPnlInputLevelEnum.custom_entities && (
              <FormEntitySelect
                shouldUnregister={false}
                isMulti={true}
                name="hypothetical_pnl_entity_ids"
                title={t(
                  "waterfall:hypotheticalWaterfall.modal.hypotheticalEntitiesSelect.label",
                )}
              />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button colorScheme="secondary" onClick={closeWithReset}>
              {t("common:cancel")}
            </Button>
            <Button type="submit">Save edits</Button>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default FundFamilyPnlLevelSelectionModal;
